import axios from "axios";
import { currentEnv } from "~/configs";
import { client } from "~/utils/medproSDK";
import { refreshTokenApi } from "~/store/login/loginApi";
import { store } from "~/store";
import { logOut, updateUserToken } from "~/store/user/userAction";
import { deleteKeyCookie, getKeyCookie, setKeyCookie } from "~/utils/func";

const RESTFULL_API_URL = currentEnv.RESTFULL_API_URL;
axios.defaults.baseURL = RESTFULL_API_URL;

let failedQueue = [];
let refreshPromise = null;

const processQueue = (error, token = null) => {
  failedQueue.forEach(promise => {
    if (token) {
      promise.resolve(token);
    } else {
      promise.reject(error);
    }
  });
  failedQueue = [];
};

// Hàm xóa session toàn cục
export function handleClearAndLogout(callback) {
  deleteKeyCookie("token");
  deleteKeyCookie("refreshToken");
  window.localStorage.removeItem("jwt");

  // Dispatch action logout để cập nhật Redux store và trigger userSaga logout
  if (store) {
    store.dispatch(logOut());
  }

  if (client) {
    client.setToken("");
  }

  if (axios.defaults.headers.common) {
    delete axios.defaults.headers.Authorization;
  }

  if (callback) {
    if (typeof callback === "function") {
      callback();
    }
    return;
  }

  // Phát một sự kiện toàn cục để Root Component bắt được và điều hướng qua React Router
  const event = new CustomEvent("unauthorized-logout");
  window.dispatchEvent(event);
}

// 1. Request Interceptor: Đính kèm token vào mọi request gửi đi
export const setupAxiosInterceptors = () => {
  // 1. Response Interceptor: Đánh chặn lỗi 401 và tự động Refresh Token
  axios.interceptors.response.use(
    response => response,
    async error => {
      const originalRequest = error.config;

      // Tránh lặp vô hạn khi API làm mới/nâng cấp token bị lỗi 401
      if (typeof window === "undefined") {
        return Promise.reject(error);
      }

      // Chỉ thực hiện refresh khi gặp lỗi 401 Unauthorized và request chưa từng được retry
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        const currentToken = getKeyCookie("token");
        let authHeader = "";
        if (originalRequest.headers) {
          if (typeof originalRequest.headers.get === "function") {
            authHeader = originalRequest.headers.get("Authorization");
          } else {
            authHeader = originalRequest.headers.Authorization || "";
          }
        }
        const requestToken = authHeader.replace("Bearer ", "");

        // NẾU đã có token mới được cập nhật trong cookie (do một request khác vừa mới refresh thành công trước đó)
        if (currentToken && requestToken && currentToken !== requestToken) {
          // Lấy luôn token mới để gửi lại request ngay lập tức, không cần gọi API refresh nữa!
          if (originalRequest.headers) {
            if (typeof originalRequest.headers.set === "function") {
              originalRequest.headers.set(
                "Authorization",
                `Bearer ${currentToken}`
              );
            } else {
              originalRequest.headers.Authorization = `Bearer ${currentToken}`;
            }
          }
          return axios(originalRequest);
        }

        if (refreshPromise) {
          try {
            const token = await new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            });

            if (originalRequest.headers) {
              if (typeof originalRequest.headers.set === "function") {
                originalRequest.headers.set("Authorization", `Bearer ${token}`);
              } else {
                originalRequest.headers.Authorization = `Bearer ${token}`;
              }
            }
            return axios(originalRequest);
          } catch (err) {
            return Promise.reject(err);
          }
        }

        const refreshToken = getKeyCookie("refreshToken");
        if (!refreshToken) {
          handleClearAndLogout();
          return Promise.reject(error);
        }

        try {
          const { token: newAccessToken } = await refreshSession(refreshToken);

          if (originalRequest.headers) {
            if (typeof originalRequest.headers.set === "function") {
              originalRequest.headers.set(
                "Authorization",
                `Bearer ${newAccessToken}`
              );
            } else {
              originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            }
          }
          console.log("check token :>> ", getKeyCookie("token"));
          return axios(originalRequest);
        } catch (refreshError) {
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );
};

export const refreshSession = async (refreshToken = "") => {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      // Gọi API refresh token
      client.setToken("");
      const response = await refreshTokenApi(
        RESTFULL_API_URL,
        { refreshToken },
        { headers: { token: "" } }
      );
      const { token: newToken, refreshToken: newRefreshToken } = response.data;

      // Lưu token mới vào Cookie (setKeyCookie sẽ tự động giải mã JWT để cấu hình expires cookies)
      setKeyCookie("token", newToken);
      setKeyCookie("refreshToken", newRefreshToken);

      // Đồng bộ token mới vào localStorage để duy trì làm chốt chặn dự phòng cho CRM Iframe
      try {
        window.localStorage.setItem("jwt", newToken);
      } catch (e) {
        console.warn("Không thể lưu localStorage trong Iframe:", e);
      }

      // Đồng bộ token mới sang Medpro SDK client instance
      client.setToken(newToken);

      // Cập nhật token mới vào Redux Store
      if (store) {
        store.dispatch(updateUserToken(newToken));
      }

      processQueue(null, newToken);
      return { token: newToken, refreshToken: newRefreshToken };
    } catch (error) {
      processQueue(error, null);
      console.error("Refresh session failed:", error);
      handleClearAndLogout();
      throw error;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};
